/**
 * CookieBanner — minimal cookie notice
 * Design: Dark Tech / Glassmorphism — matches site palette
 * Colors: #0c0c0c bg, #57d7e2 cyan accent, Barlow font
 * Compact bottom-right corner card — must never obstruct hero content.
 * Non-essential cookies are treated as declined by default; "Accept" opts in,
 * "Manage" records a decline and opens the privacy policy.
 * The choice lives in lib/cookieConsent (localStorage key
 * "indexed_cookie_consent"), which also broadcasts it so other overlays —
 * the video-review popup — know the banner has gone.
 */

import { useEffect, useState } from "react";
import { readConsent, writeConsent } from "@/lib/cookieConsent";
import { isPrerender } from "@/lib/isPrerender";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (isPrerender()) return;
    const stored = readConsent();
    if (!stored) {
      // Slight delay so the page renders first
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const dismiss = (choice: "accepted" | "declined") => {
    writeConsent(choice);
    setLeaving(true);
    setTimeout(() => setVisible(false), 400);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
      style={{
        position: "fixed",
        bottom: "16px",
        right: "16px",
        transform: leaving ? "translateY(150%)" : "translateY(0)",
        transition: "transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
        zIndex: 9999,
        maxWidth: "min(360px, calc(100vw - 32px))",
        background: "rgba(12, 12, 12, 0.92)",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        border: "1px solid rgba(87, 215, 226, 0.25)",
        borderRadius: "12px",
        padding: "14px 16px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
        fontFamily: ff,
        display: "flex",
        alignItems: "center",
        gap: "14px",
      }}
    >
      <p style={{ margin: 0, color: "rgba(255,255,255,0.85)", fontSize: "13px", fontWeight: 400, lineHeight: 1.5, flex: 1 }}>
        We use cookies to improve your experience.
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
        <a
          href="/privacy-policy"
          onClick={() => dismiss("declined")}
          style={{
            color: "rgba(255,255,255,0.55)",
            fontSize: "13px",
            fontWeight: 500,
            fontFamily: ff,
            textDecoration: "underline",
            textUnderlineOffset: "3px",
            whiteSpace: "nowrap",
            padding: "10px 4px",
          }}
        >
          Manage
        </a>
        <button type="button"
          onClick={() => dismiss("accepted")}
          style={{
            padding: "10px 20px",
            borderRadius: "88px",
            border: "1px solid #57d7e2",
            background: "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)",
            color: "#0c0c0c",
            fontSize: "13px",
            fontWeight: 600,
            fontFamily: ff,
            cursor: "pointer",
            transition: "opacity 0.2s",
            whiteSpace: "nowrap",
          }}
          onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.opacity = "0.85")}
          onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.opacity = "1")}
        >
          Accept
        </button>
      </div>
    </div>
  );
}
